/**
 * AOZServices.ts - Servicio de integración para Autorización de Operación de Zarpe (AOZ)
 * y Reglas Operacionales de Control Muelle Prat (Control MP) - Etapa 2
 */

export interface IAOZSolicitud {
  id: number
  folio: string
  codigo_verificacion: string
  canal: number
  canal_nombre: string
  nave_nombre: string
  matricula: string
  puerto?: string
  fecha_zarpe: string
  eta: string
  telefono_remitente: string
  resultado: number // 0: Recibida, 1: Vigente, 2: Pendiente, 3: Rechazada, 4: Cerrada
  codigo_resultado: string // AOZ-000 .. AOZ-016
  fecha_recepcion: string
  fecha_resolucion?: string
  documento_url?: string
  tipo_documento?: string
  patron_nombre?: string
  patron_rut?: string
  tripulacion?: Array<{ rut: string; nombre: string }>
}

export interface IAOZStatusInfo {
  vigente: boolean
  folio?: string
  codigo?: string
  resultado?: number
  codigo_resultado?: string
  fecha_zarpe?: string
  eta?: string
  motivo?: string
}

// Solicitudes operativas maestras sincronizadas con el motor de reglas
let cachedSolicitudes: IAOZSolicitud[] = [
  {
    id: 100,
    folio: '13726435',
    codigo_verificacion: 'C9A44',
    canal: 1,
    canal_nombre: 'Carga Manual',
    nave_nombre: 'LANCHA PRAT II',
    matricula: 'VAL-4892',
    puerto: 'Valparaíso',
    fecha_zarpe: '2026-10-17 10:00',
    eta: '2026-10-17 22:00',
    telefono_remitente: '+56987654321',
    resultado: 1,
    codigo_resultado: 'AOZ-001',
    fecha_recepcion: '2026-10-17 08:00',
    fecha_resolucion: '2026-10-17 08:05',
    patron_nombre: 'Carlos Mendoza',
    patron_rut: '12.345.678-9',
    tripulacion: [{ rut: '15.890.123-4', nombre: 'Juan Valenzuela' }],
  },
  {
    id: 101,
    folio: '13726433',
    codigo_verificacion: 'A9F32',
    canal: 1,
    canal_nombre: 'Carga Manual',
    nave_nombre: 'NOEMI',
    matricula: 'VAL-2431',
    puerto: 'Valparaíso',
    fecha_zarpe: '2026-10-17 12:00',
    eta: '2026-10-17 20:00',
    telefono_remitente: '+56986481744',
    resultado: 2, // Pendiente de enrolamiento
    codigo_resultado: 'AOZ-002',
    fecha_recepcion: '2026-10-17 09:30',
    patron_nombre: 'Roberto Bravo',
    patron_rut: '9.876.543-2',
    tripulacion: [{ rut: '14.234.567-8', nombre: 'Pedro Soto' }],
  },
  {
    id: 102,
    folio: '13726450',
    codigo_verificacion: 'B8C11',
    canal: 0,
    canal_nombre: 'WhatsApp',
    nave_nombre: 'DOÑA RUTH',
    matricula: 'VAL-1892',
    puerto: 'Valparaíso',
    fecha_zarpe: '2026-10-17 09:00',
    eta: '2026-10-17 23:59',
    telefono_remitente: '+56950207581',
    resultado: 1, // Vigente
    codigo_resultado: 'AOZ-001',
    fecha_recepcion: '2026-10-17 07:30',
    fecha_resolucion: '2026-10-17 07:32',
    patron_nombre: 'Juan Valenzuela',
    patron_rut: '15.890.123-4',
    tripulacion: [{ rut: '12.345.678-9', nombre: 'Carlos Mendoza' }],
  },
  {
    id: 103,
    folio: '13726430',
    codigo_verificacion: 'D1E22',
    canal: 0,
    canal_nombre: 'WhatsApp',
    nave_nombre: 'PERLA NEGRA',
    matricula: 'VAL-1029',
    puerto: 'Valparaíso',
    fecha_zarpe: '2026-10-17 08:00',
    eta: '2026-10-17 18:00',
    telefono_remitente: '+56944332211',
    resultado: 3, // Rechazada / Conflicto
    codigo_resultado: 'AOZ-009',
    fecha_recepcion: '2026-10-17 07:00',
    patron_nombre: 'Pedro Soto',
    patron_rut: '14.234.567-8',
    tripulacion: [{ rut: '9.876.543-2', nombre: 'Roberto Bravo' }],
  },
]

export class AOZServices {
  public static async fetchSolicitudes(): Promise<IAOZSolicitud[]> {
    try {
      const res = await fetch('/websrv/sw/aoz_revision_operador.php')
      const json = await res.json()
      if (json.ok && Array.isArray(json.data) && json.data.length > 0) {
        cachedSolicitudes = json.data
        return cachedSolicitudes
      }
    } catch {
      // Retorna fallback operacional
    }
    return cachedSolicitudes
  }

  public static getAOZVigenteByNave(
    nombreNave?: string,
    matricula?: string,
    idEmbarcacion?: number,
  ): IAOZStatusInfo {
    if (!nombreNave && !matricula) {
      return { vigente: false, motivo: 'Sin datos de nave' }
    }

    const clean = (s: string) =>
      s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

    const sol = cachedSolicitudes.find((s) => {
      const matchMatricula = matricula && clean(s.matricula) === clean(matricula)
      const matchNombre = nombreNave && clean(s.nave_nombre) === clean(nombreNave)
      return matchMatricula || matchNombre
    })

    if (!sol) {
      return {
        vigente: false,
        motivo: 'Sin solicitud AOZ registrada',
      }
    }

    if (sol.resultado === 1) {
      return {
        vigente: true,
        folio: sol.folio,
        codigo: sol.codigo_resultado,
        resultado: sol.resultado,
        codigo_resultado: sol.codigo_resultado,
        fecha_zarpe: sol.fecha_zarpe,
        eta: sol.eta,
      }
    }

    return {
      vigente: false,
      folio: sol.folio,
      codigo: sol.codigo_resultado,
      resultado: sol.resultado,
      codigo_resultado: sol.codigo_resultado,
      motivo:
        sol.resultado === 0
          ? 'AOZ en estado Recibida (Pendiente de Revisión)'
          : sol.resultado === 2
          ? 'AOZ Pendiente de Enrolamiento (AOZ-002)'
          : sol.resultado === 3
          ? `AOZ Rechazada (${sol.codigo_resultado})`
          : 'AOZ Cerrada / Expirada',
    }
  }

  public static updateSolicitudStatus(id: number, resultado: number, codigo: string): void {
    const s = cachedSolicitudes.find((item) => item.id === id)
    if (s) {
      s.resultado = resultado
      s.codigo_resultado = codigo
      if (resultado === 1) {
        s.fecha_resolucion = new Date().toISOString().replace('T', ' ').substring(0, 16)
      }
    }
  }
}

export default AOZServices
